const VOID_HTML_TAGS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
const UNSAFE_TAG_PATTERN = /<\s*(script|iframe|object|embed|form|input|button|video|audio|canvas|svg)\b/i;
const EVENT_HANDLER_ATTRIBUTE_PATTERN = /\son[a-z]+\s*=/i;
const UNSAFE_URL_PATTERN = /^\s*(javascript|vbscript):/i;
const DATA_URL_PATTERN = /^\s*data:/i;
const UNSUPPORTED_EMAIL_CSS_PATTERN =
    /display\s*:\s*(flex|grid)|position\s*:\s*(fixed|sticky)|background-image\s*:|@font-face|@import|filter\s*:|backdrop-filter\s*:|box-shadow\s*:/i;
const getUnsupportedEmailCssFeature = (matched: string): string => {
    const token = matched.toLowerCase().replace(/\s+/g, '');
    if (token.includes('box-shadow')) return 'box-shadow';
    if (token.includes('backdrop-filter')) return 'backdrop-filter';
    if (token.startsWith('filter:')) return 'filter';
    if (token.includes('display:flex')) return 'display: flex';
    if (token.includes('display:grid')) return 'display: grid';
    if (token.includes('position:fixed')) return 'position: fixed';
    if (token.includes('position:sticky')) return 'position: sticky';
    if (token.includes('background-image')) return 'background-image';
    if (token.includes('@font-face')) return '@font-face';
    if (token.includes('@import')) return '@import';
    return matched.trim();
};
const MEDIA_QUERY_PATTERN = /@media\b/i;

export type HtmlSourceValidationCode =
    | 'unexpected-closing-tag'
    | 'expected-closing-tag'
    | 'missing-closing-tag'
    | 'unbalanced-css-braces'
    | 'missing-css-semicolon'
    | 'unsafe-tag'
    | 'inline-event-handler'
    | 'unsafe-url-protocol'
    | 'data-url'
    | 'image-missing-alt'
    | 'image-missing-width'
    | 'unknown-placeholder'
    | 'unbalanced-placeholder-markers'
    | 'unsupported-email-css'
    | 'media-query'
    | 'table-layout-attributes'
    | 'large-email';

export interface HtmlSourceValidationMessage {
    section: string;
    level: 'error' | 'warning';
    line: number;
    code: HtmlSourceValidationCode;
    values?: Record<string, string | number>;
}

export interface ValidateHtmlSourceOptions {
    section?: string;
    placeholders?: string[];
    includeUnsafeMarkup?: boolean;
    includeImages?: boolean;
    includePlaceholders?: boolean;
    includeEmailCompatibility?: boolean;
}

const getLineNumber = (source: string, index: number): number => source.slice(0, index).split('\n').length;

const validateHtmlTags = (source: string, section: string): HtmlSourceValidationMessage[] => {
    const messages: HtmlSourceValidationMessage[] = [];
    const stack: { tag: string; line: number }[] = [];
    const tagRegex = /<\/?\s*([a-zA-Z][\w:-]*)(?:\s[^<>]*?)?>/g;
    let match: RegExpExecArray | null;

    while ((match = tagRegex.exec(source))) {
        const rawTag = match[0];
        const tag = match[1].toLowerCase();
        const line = getLineNumber(source, match.index);
        const isClosingTag = rawTag.startsWith('</');
        const isSelfClosingTag = rawTag.endsWith('/>') || VOID_HTML_TAGS.has(tag);

        if (!isClosingTag && !isSelfClosingTag) {
            stack.push({ tag, line });
            continue;
        }

        if (!isClosingTag) continue;

        const last = stack[stack.length - 1];
        if (!last) {
            messages.push({
                section,
                level: 'error',
                line,
                code: 'unexpected-closing-tag',
                values: { tag }
            });
            continue;
        }

        if (last.tag === tag) {
            stack.pop();
            continue;
        }

        messages.push({
            section,
            level: 'error',
            line,
            code: 'expected-closing-tag',
            values: { expectedTag: last.tag, actualTag: tag }
        });
    }

    stack.reverse().forEach((item) => {
        messages.push({
            section,
            level: 'error',
            line: item.line,
            code: 'missing-closing-tag',
            values: { tag: item.tag }
        });
    });

    return messages;
};

const validateCssBlocks = (source: string, section: string): HtmlSourceValidationMessage[] => {
    const messages: HtmlSourceValidationMessage[] = [];
    const styleRegex = /<style\b[^>]*>([\s\S]*?)<\/style>/gi;
    let match: RegExpExecArray | null;

    while ((match = styleRegex.exec(source))) {
        const styleContent = match[1];
        const styleOffset = match.index + match[0].indexOf(styleContent);
        const openBraces = (styleContent.match(/{/g) ?? []).length;
        const closeBraces = (styleContent.match(/}/g) ?? []).length;

        if (openBraces !== closeBraces) {
            messages.push({
                section,
                level: 'error',
                line: getLineNumber(source, styleOffset),
                code: 'unbalanced-css-braces'
            });
        }

        const ruleRegex = /\{([^{}]*)}/g;
        let ruleMatch: RegExpExecArray | null;
        while ((ruleMatch = ruleRegex.exec(styleContent))) {
            const declarations = ruleMatch[1];
            const declarationsOffset = styleOffset + ruleMatch.index + ruleMatch[0].indexOf(declarations);

            declarations.split('\n').forEach((line, index) => {
                const trimmed = line.trim();
                if (!trimmed || !trimmed.includes(':') || trimmed.endsWith(';')) return;

                messages.push({
                    section,
                    level: 'warning',
                    line: getLineNumber(source, declarationsOffset) + index,
                    code: 'missing-css-semicolon',
                    values: { declaration: trimmed }
                });
            });
        }
    }

    return messages;
};

const validateUnsafeMarkup = (source: string, section: string): HtmlSourceValidationMessage[] => {
    const messages: HtmlSourceValidationMessage[] = [];
    const unsafeTagMatch = source.match(UNSAFE_TAG_PATTERN);

    if (unsafeTagMatch?.index !== undefined) {
        messages.push({
            section,
            level: 'error',
            line: getLineNumber(source, unsafeTagMatch.index),
            code: 'unsafe-tag',
            values: { tag: unsafeTagMatch[1].toLowerCase() }
        });
    }

    const eventHandlerMatch = source.match(EVENT_HANDLER_ATTRIBUTE_PATTERN);
    if (eventHandlerMatch?.index !== undefined) {
        messages.push({
            section,
            level: 'error',
            line: getLineNumber(source, eventHandlerMatch.index),
            code: 'inline-event-handler'
        });
    }

    const urlAttributePattern = /\s(?:href|src)\s*=\s*(['"])(.*?)\1/gi;
    let urlMatch: RegExpExecArray | null;
    while ((urlMatch = urlAttributePattern.exec(source))) {
        const value = urlMatch[2];
        if (UNSAFE_URL_PATTERN.test(value)) {
            messages.push({
                section,
                level: 'error',
                line: getLineNumber(source, urlMatch.index),
                code: 'unsafe-url-protocol',
                values: { value }
            });
        } else if (DATA_URL_PATTERN.test(value)) {
            messages.push({
                section,
                level: 'warning',
                line: getLineNumber(source, urlMatch.index),
                code: 'data-url'
            });
        }
    }

    return messages;
};

const validateImages = (source: string, section: string): HtmlSourceValidationMessage[] => {
    const messages: HtmlSourceValidationMessage[] = [];
    const imageRegex = /<img\b[^>]*>/gi;
    let match: RegExpExecArray | null;

    while ((match = imageRegex.exec(source))) {
        if (!/\salt\s*=/.test(match[0])) {
            messages.push({
                section,
                level: 'warning',
                line: getLineNumber(source, match.index),
                code: 'image-missing-alt'
            });
        }

        if (!/\swidth\s*=|\sstyle\s*=\s*["'][^"']*width\s*:/i.test(match[0])) {
            messages.push({
                section,
                level: 'warning',
                line: getLineNumber(source, match.index),
                code: 'image-missing-width'
            });
        }
    }

    return messages;
};

const validatePlaceholders = (source: string, placeholders: string[], section: string): HtmlSourceValidationMessage[] => {
    const messages: HtmlSourceValidationMessage[] = [];
    const allowedPlaceholders = new Set(placeholders.map((placeholder) => placeholder.toUpperCase()));
    const placeholderPattern = /__([A-Z0-9_]+)__/g;
    let match: RegExpExecArray | null;

    while ((match = placeholderPattern.exec(source))) {
        if (!allowedPlaceholders.has(match[1])) {
            messages.push({
                section,
                level: 'warning',
                line: getLineNumber(source, match.index),
                code: 'unknown-placeholder',
                values: { placeholder: `__${match[1]}__` }
            });
        }
    }

    const markerCount = (source.match(/__/g) ?? []).length;
    if (markerCount % 2 !== 0) {
        messages.push({
            section,
            level: 'warning',
            line: 1,
            code: 'unbalanced-placeholder-markers'
        });
    }

    return messages;
};

const validateEmailCompatibility = (source: string, section: string): HtmlSourceValidationMessage[] => {
    const messages: HtmlSourceValidationMessage[] = [];
    const unsupportedCssMatch = source.match(UNSUPPORTED_EMAIL_CSS_PATTERN);

    if (unsupportedCssMatch?.index !== undefined) {
        messages.push({
            section,
            level: 'warning',
            line: getLineNumber(source, unsupportedCssMatch.index),
            code: 'unsupported-email-css',
            values: { feature: getUnsupportedEmailCssFeature(unsupportedCssMatch[0]) }
        });
    }

    const mediaQueryMatch = source.match(MEDIA_QUERY_PATTERN);
    if (mediaQueryMatch?.index !== undefined) {
        messages.push({
            section,
            level: 'warning',
            line: getLineNumber(source, mediaQueryMatch.index),
            code: 'media-query'
        });
    }

    const tableRegex = /<table\b[^>]*>/gi;
    let tableMatch: RegExpExecArray | null;
    while ((tableMatch = tableRegex.exec(source))) {
        if (!/\scellpadding\s*=|\scellspacing\s*=|\srole\s*=/i.test(tableMatch[0])) {
            messages.push({
                section,
                level: 'warning',
                line: getLineNumber(source, tableMatch.index),
                code: 'table-layout-attributes'
            });
        }
    }

    if (source.length > 100_000) {
        messages.push({
            section,
            level: 'warning',
            line: 1,
            code: 'large-email'
        });
    }

    return messages;
};

export const validateHtmlSource = (source: string, options: ValidateHtmlSourceOptions = {}): HtmlSourceValidationMessage[] => {
    const section = options.section ?? 'body';
    const messages: HtmlSourceValidationMessage[] = [...validateHtmlTags(source, section), ...validateCssBlocks(source, section)];

    if (options.includeUnsafeMarkup) {
        messages.push(...validateUnsafeMarkup(source, section));
    }
    if (options.includeImages) {
        messages.push(...validateImages(source, section));
    }
    if (options.includePlaceholders) {
        messages.push(...validatePlaceholders(source, options.placeholders ?? [], section));
    }
    if (options.includeEmailCompatibility) {
        messages.push(...validateEmailCompatibility(source, section));
    }

    return messages;
};

export const validatePlainText = (source: string, placeholders: string[] = [], section = 'body'): HtmlSourceValidationMessage[] =>
    validatePlaceholders(source, placeholders, section);
